/**
 * DataPreviewModal — модалка предпросмотра файла Dataset.
 *
 * Phase 3 (Files UX Refactor 2026-08-08): full rewrite по TZ Phase 3.
 *
 * Что делает:
 * - Загружает данные через `useColumnPreview(datasetId, limit)` (Phase 2.4),
 *   поддерживает limit=0 (все строки) без новых endpoint'ов.
 * - Селектор 20/50/100/Все — переключает лимит на лету.
 * - Header: file_name + file_type (через FileTypeIcon) + status + rows/cols/errors.
 * - Кнопка «Скачать Excel» в footer (Phase 2.2 useDownloadDataset).
 * - Состояния: loading (skeleton-like), error (через extractApiError, не JSON dump),
 *   empty (rows.length === 0).
 * - Закрытие: backdrop/Esc/X — `onClose` (как в других модалках).
 * - Ширина ~70vw на Desktop (min(70vw, 1100px)).
 * - Внутренний scroll, sticky header/footer — без потери контекста.
 *
 * Не меняет BE-контракт: GET /datasets/{id}/columns/preview?limit=N.
 * Никаких новых endpoint'ов (TZ §2 «use existing preview endpoint и его contract»).
 *
 * Используется из FilesResultsPage (Pending tab → failed/parsing preview).
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColumnDef } from '@tanstack/react-table';
import { Loader2, X } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import { FileTypeIcon } from '@/components/common/FileTypeIcon';
import { DownloadButton } from '@/components/files/DownloadButton';
import { DataTable } from '@/components/shared/DataTable';
import {
  type ColumnPreviewLimit,
  useColumnPreview,
  useDataset,
} from '@/hooks/useFiles';
import { useDownloadDataset } from '@/hooks/useDownloadDataset';
import { extractApiError } from '@/lib/apiError';
import { cn } from '@/lib/utils';

interface PreviewRow {
  _id: string;
  _idx: number;
  raw_data: Record<string, unknown>;
  displayed_data: Record<string, unknown>;
}

interface DataPreviewModalProps {
  open: boolean;
  onClose: () => void;
  datasetId: string | null;
}

type LimitKey = '20' | '50' | '100' | 'all';

const LIMIT_VALUE: Record<LimitKey, ColumnPreviewLimit> = {
  '20': 20,
  '50': 50,
  '100': 100,
  all: 0,
};

const STATUS_LABEL_KEY = {
  uploaded: 'files.status.uploaded',
  parsing: 'files.status.parsing',
  ready: 'files.status.ready',
  failed: 'files.status.failed',
} as const;

const STATUS_TONE = {
  uploaded: 'bg-surface-700/40 text-surface-300 border-surface-700',
  parsing: 'bg-info-tint/15 text-info300 border-info-tint/30',
  ready: 'bg-success-tint/15 text-success border-success-tint/30',
  failed: 'bg-danger-tint/15 text-danger border-danger-tint/30',
} as const;

export function DataPreviewModal({ open, onClose, datasetId }: DataPreviewModalProps) {
  const { t } = useTranslation();
  const [limitKey, setLimitKey] = useState<LimitKey>('20');
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const datasetQ = useDataset(open ? datasetId : null);
  const previewQ = useColumnPreview(
    open ? datasetId : null,
    LIMIT_VALUE[limitKey],
  );
  const download = useDownloadDataset();

  // Сбрасываем локальный download-error при смене dataset'а.
  useEffect(() => {
    setDownloadError(null);
  }, [datasetId]);

  // Сбрасываем к дефолту при открытии.
  useEffect(() => {
    if (open) {
      setLimitKey('20');
      setDownloadError(null);
    }
  }, [open]);

  const dataset = datasetQ.data;
  const preview = previewQ.data;
  const isLoading = datasetQ.isLoading || previewQ.isLoading;
  const error = datasetQ.error || previewQ.error;
  const errorMessage = error ? extractApiError(error, t('files.preview.loading')) : null;

  const isEmpty = !isLoading && !error && preview?.rows.length === 0;
  const rows = preview?.rows ?? [];
  const effectiveColumns = preview?.effective_columns ?? [];
  const totalRows = dataset?.total_rows ?? 0;
  const totalColumns = dataset?.total_columns ?? 0;
  const errorCount = (dataset?.raw_metadata as { error_count?: number } | null)
    ?.error_count;

  // P1.3: preview rows → DataTable. Add positional _idx/_id (preview rows have
  // no native id); columns are dynamic from effective_columns.
  const dataRows: PreviewRow[] = useMemo(
    () => rows.map((r, i) => ({ _id: `row-${i}`, _idx: i + 1, ...r })),
    [rows],
  );
  const tableColumns = useMemo<ColumnDef<PreviewRow>[]>(() => {
    const cols: ColumnDef<PreviewRow>[] = [
      {
        accessorKey: '_idx',
        header: '#',
        cell: (info) => (
          <span className="text-surface-500">{info.getValue<number>()}</span>
        ),
        size: 50,
        enableColumnFilter: false,
      },
    ];
    for (const c of effectiveColumns) {
      cols.push({
        id: c,
        accessorFn: (row) => row.displayed_data?.[c] ?? row.raw_data?.[c],
        header: c,
        cell: (info) => {
          const v = info.getValue();
          const display =
            v === null || v === undefined
              ? ''
              : typeof v === 'string'
                ? v
                : JSON.stringify(v);
          return (
            <div className="text-surface-200 max-w-md truncate" title={display}>
              {display}
            </div>
          );
        },
        size: 200,
        enableColumnFilter: false,
      });
    }
    return cols;
  }, [effectiveColumns]);

  const handleDownload = async () => {
    if (!datasetId) return;
    setDownloadError(null);
    try {
      await download.mutateAsync(datasetId);
    } catch (e) {
      setDownloadError(extractApiError(e, t('files.preview.download_error')));
    }
  };

  // === Render ===
  return (
    <Modal
      open={open}
      onClose={onClose}
      className="max-w-[min(70vw,1100px)] max-h-[85vh] flex flex-col"
    >
      {/* Header — sticky */}
      <div className="flex items-start justify-between gap-4 px-4 py-3 border-b border-surface-800">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {dataset && (
              <FileTypeIcon
                fileType={dataset.file_type}
                className="w-5 h-5"
              />
            )}
            <h2 className="text-base font-semibold text-surface-100 truncate">
              {dataset?.name ?? t('files.preview.title')}
            </h2>
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-surface-500">
            {dataset && (
              <>
                <span className="uppercase">{dataset.file_type}</span>
                <span aria-hidden>·</span>
                <span
                  className={cn(
                    'px-1.5 py-0.5 rounded border uppercase tracking-wider',
                    STATUS_TONE[dataset.status],
                  )}
                >
                  {t(STATUS_LABEL_KEY[dataset.status])}
                </span>
                <span aria-hidden>·</span>
                <span>
                  {t('files.results.rows_count', {
                    count: totalRows,
                    cols: totalColumns,
                  })}
                </span>
                {errorCount != null && errorCount > 0 && (
                  <>
                    <span aria-hidden>·</span>
                    <span className="text-warning">
                      {t('files.results.col_errors', {
                        defaultValue: 'Ошибок',
                      })}
                      : {errorCount}
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close', { defaultValue: 'Закрыть' })}
          className="text-surface-500 hover:text-surface-200 transition-colors shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Limit selector — toolbar (sticky top) */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-surface-800 flex-wrap">
        <span className="text-[11px] text-surface-500 uppercase tracking-wider">
          {t('files.preview.limit_label', { defaultValue: 'Показать:' })}
        </span>
        {(['20', '50', '100', 'all'] as LimitKey[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setLimitKey(key)}
            aria-pressed={limitKey === key}
            className={cn(
              'text-xs px-2.5 py-1 rounded border transition-colors',
              limitKey === key
                ? 'border-primary-500/50 bg-primary-500/10 text-primary-200'
                : 'border-surface-700 text-surface-300 hover:border-surface-500',
            )}
          >
            {key === 'all'
              ? t('files.preview.limit.all', { defaultValue: 'Все строки' })
              : t(`files.preview.limit.${key}`, {
                  defaultValue: `${LIMIT_VALUE[key]}`,
                })}
          </button>
        ))}
      </div>

      {/* Body — internal scroll */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div
            className="p-8 text-sm text-surface-400 flex items-center justify-center gap-2"
            data-testid="preview-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('files.preview.loading', { defaultValue: 'Загружаем предпросмотр…' })}
          </div>
        ) : errorMessage ? (
          <div
            className="p-6 text-sm text-danger"
            data-testid="preview-error"
            role="alert"
          >
            <p className="font-medium">
              {t('files.preview.error.title', {
                defaultValue: 'Не удалось загрузить предпросмотр',
              })}
            </p>
            <p className="mt-1 text-surface-400">{errorMessage}</p>
          </div>
        ) : isEmpty ? (
          <div
            className="p-8 text-sm text-surface-400 text-center"
            data-testid="preview-empty"
          >
            {t('files.preview.empty', {
              defaultValue: 'Файл не содержит строк для предпросмотра',
            })}
          </div>
        ) : (
          <DataTable columns={tableColumns} data={dataRows} pinLeftCount={1} />
        )}
      </div>

      {/* Footer — sticky bottom */}
      <div className="border-t border-surface-800 px-4 py-3 space-y-2">
        {downloadError && (
          <p
            className="text-[11px] text-danger"
            role="alert"
            data-testid="preview-download-error"
          >
            {downloadError}
          </p>
        )}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost"
          >
            {t('common.close', { defaultValue: 'Закрыть' })}
          </button>
          {/* Phase 4: единый DownloadButton (variant="primary") в footer.
              Тот же disabled/loading contract, что в Transform action bar. */}
          <DownloadButton
            dataset={dataset ?? null}
            onDownload={handleDownload}
            isDownloading={download.isPending}
            variant="primary"
            label={t('files.preview.download_button', {
              defaultValue: 'Скачать Excel',
            })}
            testId="preview-download-button"
          />
        </div>
      </div>
    </Modal>
  );
}
